import { z } from "zod";
import { ROLE_NAMES, type RoleName } from "../permissions";
import {
  AUDIT_ALERT_JOBS,
  RETENTION_REASONS,
  TENANT_GRANT_REASONS,
  VERIFY_REASONS,
} from "./auditIntegrity";

/**
 * Security-event alerts (docs/research/2026-09-28-design-security-alerts.md, owner answers W3-14
 * to W3-18 of 2026-09-28): the closed category list, the owner's defaults and threshold, the
 * classifier the runner's security sweep reads every audit row through, and the facts a
 * `security` outbox event carries.
 *
 * Pure and dependency-free beyond zod and the role list, so the runner, the server and the client
 * read the same words. It imports nothing from `notifications.ts`, which imports it (the facts
 * schema is part of the outbox's facts), so the two modules never form a cycle.
 *
 * SEC-17: a security event carries identifiers, names, a closed role, counts and UTC times. Never
 * an IP address, an issuer URL, a secret name, a refusal reason or a grant's free-text reason: the
 * audit trail shows those to the people allowed to read it.
 */

/**
 * The closed list. `identity_rebind` is the sixth, added by owner answer W3-17; `audit_integrity`
 * the seventh (the HIPAA audit controls, 2026-09-29: a broken audit chain, or a failed verify,
 * export or retention; docs/research/2026-09-28-design-hipaa-audit.md, "Alerts").
 */
export const SECURITY_CATEGORIES = [
  "break_glass",
  "admin_grant",
  "destination_change",
  "credential_change",
  "sign_in_failures",
  "identity_rebind",
  "audit_integrity",
] as const;
export type SecurityCategory = (typeof SECURITY_CATEGORIES)[number];

/**
 * Owner answer W3-14: every PlatformAdmin and TenantAdmin is subscribed to these two until they
 * switch them off; the other four are opt-in (W3-15 keeps `credential_change` opt-in, because
 * customers re-enter passwords routinely and it must not nag).
 */
export const DEFAULT_SECURITY_CATEGORIES: readonly SecurityCategory[] = [
  "break_glass",
  "sign_in_failures",
];

/**
 * Wave 4 answer Q2 (2026-09-28): an audit integrity alert always reaches every PlatformAdmin and
 * the L1 team, whatever they chose; a TenantAdmin receives one about their tenant only when they
 * switched it on (it is opt-in for them, not a default).
 */
export const ALWAYS_SENT_SECURITY_CATEGORIES: readonly SecurityCategory[] = ["audit_integrity"];

/** Whether a category reaches PlatformAdmins and the L1 team whatever they chose (Q2). */
export function isAlwaysSentSecurityCategory(category: SecurityCategory): boolean {
  return ALWAYS_SENT_SECURITY_CATEGORIES.includes(category);
}

/** Owner answer W3-16: five refusals by one presenter in 15 minutes, at most one alert an hour. */
export const SIGN_IN_FAILURE_THRESHOLD = { count: 5, windowMinutes: 15, everyMinutes: 60 } as const;

/**
 * The deployment-wide cap on per-account sign-in alerts within `everyMinutes` (review SF-2): past
 * it, every other account over the threshold is one summary alert (`accounts`), at most one per
 * `everyMinutes`, so many presenters (self-service accounts with no user row) cannot flood the
 * PlatformAdmins.
 */
export const SIGN_IN_ALERTS_PER_HOUR = 10;

/** The subject prefix word. Failure-level categories colour the Teams card red. */
export const SECURITY_SEVERITIES = ["failure", "warning"] as const;
export type SecuritySeverity = (typeof SECURITY_SEVERITIES)[number];

export const SECURITY_CATEGORY_SEVERITY = {
  break_glass: "failure",
  admin_grant: "warning",
  destination_change: "warning",
  credential_change: "warning",
  sign_in_failures: "failure",
  identity_rebind: "warning",
  audit_integrity: "failure",
} as const satisfies Record<SecurityCategory, SecuritySeverity>;

/** The switch's label on My alerts, the L1 card and the deliveries card. */
export const SECURITY_CATEGORY_LABELS = {
  break_glass: "Self-granted access (break-glass)",
  admin_grant: "Admin access granted",
  destination_change: "Destination changed",
  credential_change: "Connection or channel secret changed",
  sign_in_failures: "Repeated failed sign-ins",
  identity_rebind: "Sign-in moved to another provider",
  audit_integrity: "Audit trail integrity",
} as const satisfies Record<SecurityCategory, string>;

/** The one line under each switch. */
export const SECURITY_CATEGORY_DESCRIPTIONS = {
  break_glass:
    "Someone granted access to themselves, or an operator reset a person's sign-in on the server.",
  admin_grant:
    "Someone was made a platform, tenant or instance admin, or had that access restored.",
  destination_change: "An instance's destination or its credentials were changed.",
  credential_change:
    "A connection's secret or a Teams webhook was replaced. Off by default, because routine password updates would each send one.",
  sign_in_failures:
    "One account was refused sign-in 5 times within 15 minutes. At most one alert an hour for each account.",
  identity_rebind: "Someone moved a person's sign-in to another identity provider.",
  audit_integrity:
    "An audit trail failed its daily integrity check, or its write-once export or retention failed. Always sent to platform admins and the L1 team.",
} as const satisfies Record<SecurityCategory, string>;

/** The categories a person receives: their saved choice, else the defaults (W3-14). */
export function effectiveSecurityCategories(
  saved: readonly string[] | null | undefined,
): SecurityCategory[] {
  const chosen = saved ?? DEFAULT_SECURITY_CATEGORIES;
  return SECURITY_CATEGORIES.filter((category) => chosen.includes(category));
}

// ---------------------------------------------------------------------------------------------
// The classifier: one audit row, one category or none
// ---------------------------------------------------------------------------------------------

/** The columns of an audit row the classifier reads (either chain; both have them). */
export interface SecurityAuditRow {
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
}

/** The admin roles whose grant is an `admin_grant`. Viewer and Contributor grants are not alerts. */
export const ADMIN_ROLE_NAMES: readonly RoleName[] = [
  "PlatformAdmin",
  "TenantAdmin",
  "InstanceAdmin",
];

/** The audit actions the sweep reads; everything else is never selected. */
export const SECURITY_AUDIT_ACTIONS = [
  "access.grant",
  "access.reactivate",
  "binding.update",
  "secret.write",
  "secret.delete",
  "user.rebind",
] as const;

function metadataOf(row: SecurityAuditRow): Record<string, unknown> {
  return row.metadata && typeof row.metadata === "object" ? row.metadata : {};
}

/** A positive whole count in the metadata, or null. */
export function metadataCount(row: SecurityAuditRow, key: string): number | null {
  const value = metadataOf(row)[key];
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;
}

/** A grant or reactivation: break-glass, an admin role's grant, or no alert. */
function grantCategory(
  row: SecurityAuditRow,
  metadata: ReturnType<typeof metadataOf>,
): SecurityCategory | null {
  if (row.action === "access.grant" && metadata.break_glass === true) return "break_glass";
  const role = metadata.role_name;
  return typeof role === "string" && (ADMIN_ROLE_NAMES as readonly string[]).includes(role)
    ? "admin_grant"
    : null;
}

/** A rebind: break-glass SQL, the bulk route's batch row when it moved someone, or one person. */
function rebindCategory(
  row: SecurityAuditRow,
  metadata: ReturnType<typeof metadataOf>,
): SecurityCategory | null {
  if (metadata.break_glass === true) return "break_glass";
  if (row.entityType === "user_batch") {
    const moved = metadataCount(row, "count");
    return moved !== null && moved > 0 ? "identity_rebind" : null;
  }
  return metadata.batch === true ? null : "identity_rebind";
}

/**
 * Which category an audit row is, or null. Pure. The rules (design "Categories"):
 * - an attempt the capture recorded as failed (`metadata.outcome = "failed"`) changed nothing;
 * - `access.grant` with `break_glass = true` is break-glass whatever the role, and so is the
 *   operator's break-glass SQL (a `user.rebind` row with `break_glass = true`, owner answer W3-18);
 * - `access.grant` or `access.reactivate` of an admin role is `admin_grant`; Viewer and Contributor
 *   grants and every revoke are not alerts (removing access is the safe direction);
 * - `binding.update`, and a `secret.write` of a destination field (`metadata.binding_field`), are
 *   `destination_change`; the sweep merges them per request;
 * - any other `secret.write` or `secret.delete` (a connection field, a Teams webhook, a platform
 *   setting's secret) is `credential_change`;
 * - `user.rebind` is `identity_rebind`: the single route's row, or the bulk route's batch row when
 *   it moved someone; the bulk route's per-person rows (`batch = true`) ride on the batch row.
 * A refused sign-in is never an alert of its own: the threshold counts them.
 */
export function securityCategoryOf(row: SecurityAuditRow): SecurityCategory | null {
  const metadata = metadataOf(row);
  if (metadata.outcome === "failed") return null;
  switch (row.action) {
    case "access.grant":
    case "access.reactivate":
      return grantCategory(row, metadata);
    case "binding.update":
      return "destination_change";
    case "secret.write":
    case "secret.delete":
      if (row.entityType === "instance_binding" && typeof metadata.binding_field === "string") {
        return "destination_change";
      }
      return "credential_change";
    case "user.rebind":
      return rebindCategory(row, metadata);
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------------------------

/** Names are cut to this many characters, as every notification fact name is (`FACT_NAME_MAX`). */
export const SECURITY_NAME_MAX = 80;

/**
 * An audit correlation id, when it is an identifier. The header is client-supplied, so text that
 * is not one is left out of the facts rather than carried into a message.
 */
export const SECURITY_CORRELATION_ID = /^[A-Za-z0-9._:-]{1,128}$/;

const securityName = z.string().max(SECURITY_NAME_MAX);

/**
 * What a `security` event says. `actorUserId` is read by expansion only ("not the actor") and is
 * never printed. `count` is the credentials a destination save replaced, the refusals in the
 * window, or the people a bulk rebind moved. `accounts` marks the sign-in cap's summary.
 */
export const securityFactsSchema = z
  .object({
    category: z.enum(SECURITY_CATEGORIES),
    /** Who did it: a display name or a masked address; "A server operator" for the SQL runbook. */
    actorLabel: securityName.optional(),
    actorUserId: z.uuid().optional(),
    /** Who or what it was done to: a masked address, or an instance's name. */
    subjectLabel: securityName.optional(),
    roleName: z.enum(ROLE_NAMES).optional(),
    count: z.number().int().nonnegative().optional(),
    windowMinutes: z.number().int().positive().optional(),
    /** The hourly cap's summary only: the accounts over the threshold it covers (SF-2). */
    accounts: z.number().int().positive().optional(),
    occurredAt: z.iso.datetime(),
    correlationId: z.string().regex(SECURITY_CORRELATION_ID).optional(),
    /**
     * `audit_integrity` only: which job failed and why, from closed lists, and the first bad
     * sequence number of a broken chain. Never a row's content (SEC-17).
     */
    audit: z
      .object({
        job: z.enum(AUDIT_ALERT_JOBS),
        reason: z
          .union([z.enum(VERIFY_REASONS), z.enum(RETENTION_REASONS), z.enum(TENANT_GRANT_REASONS)])
          .optional(),
        firstBadSeq: z
          .string()
          .regex(/^\d{1,19}$/)
          .optional(),
        /**
         * #1700, the two inherited grant reasons only: the `inheritedGrants` entry's `expires` day
         * (the earliest, when several), judged at its end in Pacific time.
         */
        expiresOn: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type SecurityFacts = z.infer<typeof securityFactsSchema>;

/**
 * One event's severity: its category's, except that a failed audit export or retention is a
 * warning (the chain itself is intact; design "Alerts"), and so is an inherited grant's exception
 * that has not ended yet (#1700: still allowed, a notice before its date).
 */
export function securitySeverityOf(
  security: Pick<SecurityFacts, "category" | "audit">,
): SecuritySeverity {
  if (
    security.category === "audit_integrity" &&
    security.audit &&
    security.audit.job !== "verify" &&
    security.audit.job !== "tenant_grants"
  ) {
    return "warning";
  }
  if (
    security.category === "audit_integrity" &&
    security.audit?.job === "tenant_grants" &&
    security.audit.reason === "inherited_grant_expiring"
  ) {
    return "warning";
  }
  return SECURITY_CATEGORY_SEVERITY[security.category];
}

// ---------------------------------------------------------------------------------------------
// The subscription routes (`/me/notifications/security`)
// ---------------------------------------------------------------------------------------------

const categoryList = z
  .array(z.enum(SECURITY_CATEGORIES))
  .max(SECURITY_CATEGORIES.length)
  .refine((list) => new Set(list).size === list.length, "Each category once.");

/** `PUT /me/notifications/security`: the categories kept on. No user id: only your own. */
export const putSecurityPrefsBodySchema = z.object({ categories: categoryList }).strict();
export type PutSecurityPrefsBody = z.infer<typeof putSecurityPrefsBodySchema>;

export const securityPrefsViewSchema = z.object({
  /** The categories the caller receives now: the saved choice, else the defaults. */
  categories: z.array(z.enum(SECURITY_CATEGORIES)),
  /** False while the caller has never saved: the defaults are in force. */
  saved: z.boolean(),
  /** What the alerts cover: the whole deployment for a PlatformAdmin, else the tenants named. */
  scope: z.object({ deployment: z.boolean(), tenantNames: z.array(z.string()) }),
  /** The caller's account address, masked; null when the account has none. */
  callerEmailMasked: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type SecurityPrefsView = z.infer<typeof securityPrefsViewSchema>;

/** "Security alerts saved. They apply to the next event." */
export const SECURITY_PREFS_SAVED_MESSAGE = "Security alerts saved. They apply to the next event.";
